import React from 'react';
import {
  CREATIVE_EXPORT_SIZING,
  type MerchantCreativeFormat,
  type MerchantLaunchConcept,
} from '../../content/facebookMerchantLaunchBundle';

interface FacebookMerchantCreativeCardProps {
  concept: MerchantLaunchConcept;
  format: MerchantCreativeFormat;
  /**
   * Bare mode renders only the creative itself (no card chrome / labels) at the
   * fixed CSS width used for PNG export. Used by the `/preview/creative-export`
   * route so the export script can screenshot a clean creative.
   */
  bare?: boolean;
}

type CreativeLayout = {
  /** Aspect-ratio utility for the creative frame. */
  frame: string;
  /** Non-bare max width utility. */
  maxWidth: string;
  /** framed-device concept: inset of the device card. */
  framedDevice: string;
  /** framed-device concept: image scale inside the device card. */
  framedImageScale: string;
  /** product-focus concept: background image object-position. */
  productFocusObject: string;
  /** Text card width + padding. */
  textCard: string;
  /** Headline font size. */
  headline: string;
  /** Support-chip row max width. */
  chipWrap: string;
  /** How many support chips to show. */
  chipCount: number;
};

/**
 * Per-aspect-ratio layout tuning. Each ratio gets its own balanced text,
 * padding and chip count rather than cropping a single layout.
 */
const LAYOUT: Record<MerchantCreativeFormat['aspectRatio'], CreativeLayout> = {
  '4:5': {
    frame: 'aspect-[4/5]',
    maxWidth: 'max-w-[25rem]',
    framedDevice: 'inset-y-3 right-3 w-[42%]',
    framedImageScale: 'scale-[1.24]',
    productFocusObject: 'object-[center_42%]',
    textCard: 'max-w-[18rem] px-4 py-3',
    headline: 'text-[2.15rem]',
    chipWrap: 'max-w-[18rem]',
    chipCount: 3,
  },
  '1:1': {
    frame: 'aspect-[1/1]',
    maxWidth: 'max-w-[24rem]',
    framedDevice: 'inset-y-3 right-3 w-[52%]',
    framedImageScale: 'scale-[1.32]',
    productFocusObject: 'object-[center_42%]',
    textCard: 'max-w-[19rem] px-4 py-3',
    headline: 'text-[1.95rem]',
    chipWrap: 'max-w-[19rem]',
    chipCount: 3,
  },
  '9:16': {
    frame: 'aspect-[9/16]',
    maxWidth: 'max-w-[22rem]',
    framedDevice: 'bottom-4 left-1/2 top-[38%] w-[72%] -translate-x-1/2',
    framedImageScale: 'scale-[1.18]',
    // 9:16 is far enough from the source aspect ratios of our product photos
    // that the default center crop slices secondary cards mid-word at the
    // edges. Bias toward the primary product (left of frame) so the side
    // content gets pushed off-canvas instead of clipped.
    productFocusObject: 'object-[25%_50%]',
    textCard: 'max-w-[13rem] px-4 py-3',
    headline: 'text-[2rem]',
    chipWrap: 'max-w-[14rem]',
    chipCount: 2,
  },
};

/** The creative itself — shared by the preview card and the export route. */
const Creative: React.FC<FacebookMerchantCreativeCardProps> = ({
  concept,
  format,
  bare,
}) => {
  const layout = LAYOUT[format.aspectRatio];
  const sizing = bare
    ? { width: `${CREATIVE_EXPORT_SIZING[format.aspectRatio].cssWidth}px` }
    : undefined;

  return (
    <div
      data-export-id={format.id}
      style={sizing}
      className={`relative overflow-hidden rounded-[1.75rem] border border-[#ead7c6] bg-gradient-to-br ${concept.accent} ${layout.frame} ${
        bare ? '' : `mx-auto w-full ${layout.maxWidth}`
      }`}
    >
      {concept.previewStyle === 'framed-device' ? (
        <>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,249,238,0.22),transparent_28%),linear-gradient(180deg,rgba(83,47,18,0.02),rgba(83,47,18,0.36))]" />
          <div
            className={`absolute overflow-hidden rounded-[2rem] border border-[#f2deca] bg-[linear-gradient(180deg,rgba(255,251,245,0.94),rgba(250,236,216,0.86))] shadow-[0_24px_60px_rgba(92,57,25,0.22)] ${layout.framedDevice}`}
          >
            <img
              src={concept.backgroundImage}
              alt=""
              className={`h-full w-full ${layout.framedImageScale} object-contain object-center`}
            />
          </div>
        </>
      ) : concept.previewStyle === 'product-focus' ? (
        <>
          <img
            src={concept.backgroundImage}
            alt=""
            className={`absolute inset-0 h-full w-full object-cover ${layout.productFocusObject}`}
          />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(33,18,12,0.28)_0%,rgba(33,18,12,0.18)_26%,rgba(33,18,12,0.74)_100%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,247,236,0.22),transparent_28%)]" />
        </>
      ) : (
        <>
          {/*
            Ambient style: a softly-blurred, low-opacity product photo behind
            the curated text card. The blur is load-bearing — without it,
            sharp text in the source (e.g. the pos-mode merchant-tools panel)
            reads through as recognizable cropped words, which makes the ad
            look broken when FB renders it at a different aspect ratio. Blur
            collapses words into atmospheric color/shape without changing the
            creative intent.
          */}
          <img
            src={concept.backgroundImage}
            alt=""
            className={`absolute inset-0 h-full w-full object-cover opacity-25 blur-sm ${concept.ambientObjectPosition ?? ''}`}
          />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.18),transparent_30%),linear-gradient(180deg,rgba(23,16,11,0.32),rgba(23,16,11,0.74))]" />
        </>
      )}

      <div className="relative flex h-full flex-col justify-between p-5 pb-7 text-white sm:p-6 sm:pb-9">
        <div
          className={`rounded-[1.35rem] border border-white/15 bg-white/10 backdrop-blur-sm ${layout.textCard}`}
        >
          <p className="text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-white/75">
            Coupling WNC
          </p>
          <h3 className={`mt-3 font-bold leading-tight ${layout.headline}`}>
            {format.headline}
          </h3>
          <p className="mt-3 text-sm leading-relaxed text-white/84">
            {format.subheadline}
          </p>
        </div>

        <div>
          <div className={`flex flex-wrap gap-2 ${layout.chipWrap}`}>
            {concept.supportChips.slice(0, layout.chipCount).map((chip) => (
              <span
                key={`${format.id}-${chip}`}
                className="rounded-full border border-white/14 bg-black/18 px-3 py-1.5 text-xs font-medium text-white/92 backdrop-blur-sm"
              >
                {chip}
              </span>
            ))}
          </div>

          <div className="mt-4 flex items-end">
            <div className="rounded-full border border-white/16 bg-[#f0b748] px-4 py-2 text-sm font-semibold text-[#47260f] shadow-[0_12px_24px_rgba(240,183,72,0.24)]">
              {format.footer}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const FacebookMerchantCreativeCard: React.FC<
  FacebookMerchantCreativeCardProps
> = ({ concept, format, bare }) => {
  if (bare) {
    return <Creative concept={concept} format={format} bare />;
  }

  return (
    <article className="rounded-[2rem] border border-[#e4d4c4] bg-[#fffaf4] p-4 shadow-[0_24px_60px_rgba(79,44,24,0.10)]">
      <div className="mb-4 flex items-center justify-between gap-3 px-1">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-neutral-500">
            {format.label}
          </p>
          <p className="mt-1 text-sm font-medium text-neutral-600">
            {format.placementLabel}
          </p>
        </div>
        <span className="rounded-full bg-[#f6e6d6] px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-[#8e4a28]">
          {concept.angle}
        </span>
      </div>

      <Creative concept={concept} format={format} />

      <p className="mt-4 text-xs font-medium text-neutral-500">
        Export file:{' '}
        <span className="font-semibold text-neutral-700">
          {format.exportFileName}
        </span>
      </p>
    </article>
  );
};

export default FacebookMerchantCreativeCard;
